import { Button, Typography } from '@bowtie-ins/bowkit';
import { Close } from '@bowtie-ins/bowkit/icons';
import { BowtieWebPath } from '@bowtie-web/common';
import {
  Box,
  Dialog,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import Link from '@material-ui/core/Link';
import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useUserData } from '../../contexts/UserAuthContext';
import { makeAppUrl } from '../../helpers/queryparam';

const NoUserSaveCouponModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { locale } = useIntl();
  const theme = useTheme();
  const watchMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const userData = useUserData();

  const isInIframe = window.parent !== window;

  const buildUrlWithNext = useCallback(
    (targetPath: string) => {
      const baseUrl = makeAppUrl(targetPath, locale);
      const currentUrl =
        isInIframe && userData.authenticationCallbackUrl
          ? userData.authenticationCallbackUrl
          : window.location.href;
      const nextUrl = encodeURIComponent(currentUrl);
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}next=${nextUrl}`;
    },
    [isInIframe, userData.authenticationCallbackUrl, locale],
  );

  const handleSignupClick = useCallback(() => {
    if (!isInIframe) {
      window.location.href = buildUrlWithNext(BowtieWebPath.App.Register);
    }
  }, [isInIframe, buildUrlWithNext]);

  const handleLoginClick = useCallback(() => {
    if (!isInIframe) {
      window.location.href = buildUrlWithNext(BowtieWebPath.App.Login);
    }
  }, [isInIframe, buildUrlWithNext]);

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <Box
        p={watchMobile ? 2 : 3}
        height="100%"
        display="flex"
        flexDirection="column"
      >
        <Box display="flex" alignItems="center" justifyContent="flex-end">
          <IconButton
            disableRipple
            onClick={onClose}
            style={{ width: 30, height: 30 }}
            data-ui-component="Button"
            data-ui-variant="Save Coupon user register entry"
            data-ui-action="Close"
          >
            <Close />
          </IconButton>
        </Box>

        <Box height="100%" display="grid" style={{ placeItems: 'center' }}>
          <Box textAlign="center">
            <Box mb={3}>
              <Box mb={1}>
                <Typography variant="headline-s">
                  <FormattedMessage id="coupon.save_modal.title" />
                </Typography>
              </Box>
              <Typography variant="body-l">
                <FormattedMessage id="coupon.save_modal.subtitle" />
              </Typography>
            </Box>

            <Box mb={2.5}>
              {isInIframe ? (
                <Button
                  as="a"
                  href={buildUrlWithNext(BowtieWebPath.App.Register)}
                  target="_parent"
                  rel="noopener noreferrer"
                  variant="contained"
                  fullWidth
                  size="s"
                  style={{ textDecoration: 'none' }}
                >
                  <FormattedMessage id="coupon.save_modal.signup_button" />
                </Button>
              ) : (
                <Button
                  variant="contained"
                  fullWidth
                  size="s"
                  onClick={handleSignupClick}
                >
                  <FormattedMessage id="coupon.save_modal.signup_button" />
                </Button>
              )}
            </Box>

            {isInIframe ? (
              <Link
                variant="caption"
                underline="always"
                href={buildUrlWithNext(BowtieWebPath.App.Login)}
                target="_parent"
                rel="noopener noreferrer"
              >
                <FormattedMessage id="coupon.save_modal.login_link" />
              </Link>
            ) : (
              <Link
                component="button"
                variant="caption"
                underline="always"
                onClick={handleLoginClick}
              >
                <FormattedMessage id="coupon.save_modal.login_link" />
              </Link>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default NoUserSaveCouponModal;
